/**
 * Natural-language action narrative for the poker-table SpotCard center
 * text box. Replaces the older dot-separated compact format inside the
 * table felt — the table view wants prose-style sentences for readability.
 *
 * Pure function — no React, no DOM. Tested in handHistoryNL.test.ts.
 *
 * Style:
 *   "BTN opened 2.5bb. You 3-bet to 8bb. BTN called."
 *   "Checked to you on the turn."
 *
 * Verb selection is context-aware:
 *   - Preflop, first voluntary raise → "opened"
 *   - Preflop, 2nd raise → "3-bet"; 3rd → "4-bet"; 4th → "5-bet"
 *   - Preflop, call with no prior raise → "limped"
 *   - Postflop, first wager of street → "bet"
 *   - Postflop, subsequent raise → "raised"
 *   - All other calls/checks/folds → "called"/"checked"/"folded"
 *
 * Hero shows as "You"; everyone else as their position label.
 */

import { formatBb } from "@/shared/format";
import type { SpotHistoryEntry, Street } from "@/shared/models";

const STREET_ORDER: Street[] = ["preflop", "flop", "turn", "river"];

/** Map preflop raise count → ordinal name. 0-indexed so the 1st raise is at index 0. */
const PREFLOP_RAISE_VERBS = ["opened", "3-bet", "4-bet", "5-bet", "6-bet"] as const;

/** Per-street group of narrative sentences. The component renders one
 *  line per group with a small street label so multi-street context is
 *  scannable at a glance. */
export interface StreetNarrative {
  street: Street;
  text: string;
}

/**
 * Build the narrative for the action history, grouped by street.
 * Returns one entry per street that has at least one action.
 *
 * Strategy:
 * - Walk history in order, classifying each entry against the actions
 *   that came before it on the same street (so verb selection is correct).
 * - Group sentences by street; emit one StreetNarrative per non-empty
 *   street, in chronological order (preflop → river).
 * - No truncation — multi-street context is load-bearing for decisions,
 *   so the caller renders the full narrative even if it's 10+ sentences.
 */
export function historyToSentences(
  history: SpotHistoryEntry[],
  currentStreet: Street,
): StreetNarrative[] {
  if (history.length === 0) return [];

  const currentStreetIndex = STREET_ORDER.indexOf(currentStreet);
  const relevant = history.filter((entry) => {
    const entryStreetIndex = entry.street
      ? STREET_ORDER.indexOf(entry.street)
      : currentStreetIndex;
    return entryStreetIndex <= currentStreetIndex;
  });

  if (relevant.length === 0) return [];

  // Bucket sentences by their street so we can return one prose line
  // per street rather than one mashed-together paragraph.
  const byStreet = new Map<Street, string[]>();
  for (let i = 0; i < relevant.length; i++) {
    const entry = relevant[i]!;
    const street = entry.street ?? currentStreet;
    const priorOnSameStreet = relevant
      .slice(0, i)
      .filter((p) => (p.street ?? currentStreet) === street);
    const sentence = formatEntry(entry, priorOnSameStreet, street);
    if (!byStreet.has(street)) byStreet.set(street, []);
    byStreet.get(street)!.push(sentence);
  }

  return STREET_ORDER.filter((s) => byStreet.has(s)).map((s) => ({
    street: s,
    text: byStreet.get(s)!.join(" "),
  }));
}

/**
 * Format one history entry into a sentence ending with a period.
 * Public for tests; the recommended entry point is historyToSentences.
 */
export function formatEntry(
  entry: SpotHistoryEntry,
  priorOnSameStreet: SpotHistoryEntry[],
  street: Street,
): string {
  const actor = actorLabel(entry.actor);
  const verb = pickVerb(entry, priorOnSameStreet, street);
  const amountPart = formatAmount(entry, verb);
  // Join with spaces; no comma between verb and amount even when amount is empty.
  return `${actor} ${verb}${amountPart}.`;
}

function actorLabel(actor: string): string {
  return actor === "Hero" ? "You" : actor;
}

/** Pick the right verb based on action type + preceding context. */
function pickVerb(
  entry: SpotHistoryEntry,
  priorOnSameStreet: SpotHistoryEntry[],
  street: Street,
): string {
  switch (entry.action) {
    case "fold":
      return "folded";
    case "check":
      return "checked";
    case "call":
      // Preflop call with no prior raise = limp. (Big-blind option is
      // technically not a "limp" but it shows up the same way in our
      // history shape; "called" reads fine for either.)
      if (street === "preflop") {
        const priorRaisesOrBets = priorOnSameStreet.filter(
          (p) => p.action === "raise" || p.action === "bet",
        );
        if (priorRaisesOrBets.length === 0 && entry.actor !== "Hero") {
          return "limped";
        }
      }
      return "called";
    case "bet":
      // Postflop only — "bet" should never appear on preflop in well-
      // formed history (preflop wagers are raises). Be permissive.
      return "bet";
    case "raise": {
      if (street === "preflop") {
        const priorRaiseCount = priorOnSameStreet.filter(
          (p) => p.action === "raise",
        ).length;
        // priorRaiseCount=0 → opened; 1 → 3-bet; 2 → 4-bet; etc.
        const idx = Math.min(priorRaiseCount, PREFLOP_RAISE_VERBS.length - 1);
        return PREFLOP_RAISE_VERBS[idx]!;
      }
      return "raised";
    }
  }
}

/** Format the amount fragment, including the appropriate connector word. */
function formatAmount(entry: SpotHistoryEntry, verb: string): string {
  if (entry.amount == null) return "";
  const bb = formatBb(entry.amount);
  // Verbs that take "to" (sizing-style): raised, 3-bet, 4-bet, opened (raise to N).
  // Verbs that take a direct amount: bet, called.
  // Verbs that take nothing: checked, folded, limped.
  const TO_VERBS = new Set([
    "opened",
    "3-bet",
    "4-bet",
    "5-bet",
    "6-bet",
    "raised",
  ]);
  if (TO_VERBS.has(verb)) {
    return ` to ${bb}`;
  }
  if (verb === "bet" || verb === "called") {
    return ` ${bb}`;
  }
  // checked / folded / limped → no amount even if one is present
  return "";
}

